import styled from '@emotion/styled';
import { ComponentProps, MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GalleryItem } from '../../apiService';
import { IconClose } from '../../icons';
import Portal from '../../Portal';
import Scroller, { NavArrow } from '../../Scroller';
import { Box, Card, CardImage, Flex, Icon, IconButton, Image, Link, Row, Text } from '../../styles';
import { ResponsiveCols } from '../../styles/Row';
import { responsiveProps, SpacingIndex } from '../../theme';
import useScrollLock from '../../useScrollLock';
import { SpacingProps } from '../../withStyleSystem/withSpacingStyles';
import Swipe from 'react-easy-swipe';
import { Transition } from 'react-transition-group';
import { keyframes } from '@emotion/react';
import { __t } from '../../i18n';

const ContentGallery = ({ items }: ContentGalleryProps) => {
  const [active, setActive] = useState<string>();
  const activeItem = useMemo(() => items.find((i) => i.id === active), [active, items]);
  const open = activeItem !== undefined;
  useScrollLock(open);

  const handleClick = (id: string) => setActive(id);

  return (
    <Box pr={{ md: 1 }}>
      <Text variant="h2" weight="medium" mb={1} className="desktop-only">
        &nbsp;
      </Text>
      <ThumbRow items={items} cols={{ xs: 2, lg: 3 }} onClick={handleClick} />
      {open && <ImageZoom items={items} activeItem={activeItem} onClick={handleClick} onClose={() => setActive(undefined)} />}
    </Box>
  );
};

const ThumbRow = ({ items, active, cols, onClick: handleClick, cancelPadding, background, ...spacing }: ThumbRowProps & SpacingProps) => (
  <Scroller cancelPadding={cancelPadding} background={background} arrows>
    <Row cols={cols} {...spacing}>
      {items.map((item) => (
        <Thumb key={item.id} active={item.id === active} onClick={handleClick} {...item} />
      ))}
    </Row>
  </Scroller>
);

const Thumb = ({ image, id, title, active, onClick }: ThumbProps) => {
  const [supportScroll, setSupportScroll] = useState(false);

  useEffect(() => {
    setSupportScroll('scrollBehavior' in document?.documentElement?.style);
  }, []);

  useEffect(() => {
    if (!active) return;

    if (supportScroll) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    else {
      const el = document.querySelector(`${ThumbCard}.active`);
      const timeoutId = setTimeout(() => {
        if (el) el.scrollIntoView();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const ref = useRef<HTMLDivElement>(null);

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    onClick(id);
  };

  return (
    <ThumbCard
      ref={ref}
      as={Link}
      href="#"
      orientation="horizontal"
      onClick={handleClick}
      active={active}
    >
      <CardImage src={image.path + '?w=300'} alt={title} />
    </ThumbCard>
  );
};

const transitionDuration = 300;

const ImageZoom = ({ items, activeItem, onClick, onClose: handleClose }: ImageZoomProps) => {
  const [direction, setDirection] = useState<'left' | 'right'>('left');

  const handleClick = useCallback(
    (id: string) => {
      onClick(id);
      const index = (activeItem && items.indexOf(activeItem)) || 0;
      const newIndex = items.findIndex((i) => i.id === id);

      if (newIndex > index) setDirection('left');
      if (newIndex < index) setDirection('right');
    },
    [activeItem, items, onClick],
  );

  const handleNav = useCallback(
    (diff: number) => {
      if (!activeItem) return;

      const index = items.indexOf(activeItem);
      let newIndex = index + diff;
      if (newIndex > items.length - 1) newIndex = 0;
      if (newIndex < 0) newIndex = items.length - 1;

      if (diff > 0) setDirection('left');
      if (diff < 0) setDirection('right');

      const newItem = items[newIndex];
      handleClick(newItem.id);
    },
    [activeItem, handleClick, items],
  );

  const onKeyUp = useCallback(
    ({ code }: KeyboardEvent) => {
      switch (code) {
        case 'ArrowLeft':
          return handleNav(+1);
        case 'ArrowRight':
          return handleNav(-1);
        case 'Escape':
        case 'KeyX':
        case 'KeyQ':
          return handleClose();
      }
    },
    [handleClose, handleNav],
  );

  useEffect(() => {
    window.addEventListener('keyup', onKeyUp);
    return () => window.removeEventListener('keyup', onKeyUp);
  }, [onKeyUp]);

  return (
    <Portal>
      <ZoomContainer
        direction="column"
        justify="space-between"
        onClick={(e) => {
          e.stopPropagation();
          const classes = [ZoomContainer, ImageWrapper, NavArrowsContainer].map((c) => String(c).replace('.', ''));
          const shouldClose = classes.some((c) => (e.target as HTMLDivElement).classList.contains(c));
          if (shouldClose) handleClose();
        }}
      >
        <CloseButton onClick={handleClose} />
        <Swipe className="swipe-container" onSwipeLeft={() => handleNav(-1)} onSwipeRight={() => handleNav(1)}>
          <NavArrows onClick={handleNav} />
          <TransitionWrapper>
            {items.map((item) => (
              <Transition key={item.id} in={item.id === activeItem?.id} unmountOnExit timeout={{ enter: 0, exit: transitionDuration }}>
                {(state) => (
                  <ImageWrapper style={getTransitionStyles(state, direction)} align="center">
                    <ZoomedImage src={item.image?.path} />
                  </ImageWrapper>
                )}
              </Transition>
            ))}
          </TransitionWrapper>
        </Swipe>
        <Box p={2}>
          <ThumbRow
            items={items}
            onClick={handleClick}
            active={activeItem?.id}
            background={900}
            cancelPadding={2}
            p=".125rem"
            cols={{
              xs: 2,
              sm: 5,
              md: 5,
              lg: 6,
              xl: 8,
            }}
          />
        </Box>
      </ZoomContainer>
    </Portal>
  );
};

const NavArrows = ({ onClick: handleNav }: NavArrowsProps) => (
  <NavArrowsContainer justify="space-between">
    <NavArrow right onClick={() => handleNav(-1)} />
    <NavArrow left onClick={() => handleNav(+1)} />
  </NavArrowsContainer>
);

const CloseButton = ({ onClick: handleClick }) => (
  <Box style={{ width: 'max-content' }}>
    <IconButton onClick={handleClick} p={2} aria-label={__t('player.close')}>
      <Icon size="large" cursor="pointer">
        <IconClose />
      </Icon>
    </IconButton>
  </Box>
);

const getTransitionStyles = (state: string, direction: 'right' | 'left') => {
  switch (state) {
    case 'entering':
      return { opacity: 0, transform: `translateX(${direction === 'right' ? '' : '-'}50%)` };
    case 'entered':
      return { opacity: 1, transform: 'translateX(0)' };
    case 'exiting':
      return { opacity: 0, transform: `translateX(${direction === 'right' ? '-' : ''}50%)` };
    case 'exited':
      return { opacity: 0, transform: `translateX(${direction === 'right' ? '' : '-'}50%)` };
  }
};

const ThumbCard = styled(Card)<{ active?: boolean; }>(({ active }) => ({
  outline: '.125rem solid transparent',
  outlineColor: active ? 'white' : undefined,
  transition: 'outline .3s ease',
  '&:focus': {
    outline: '.125rem solid white',
  },
}));

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(1.05);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const ZoomContainer = styled(Flex)(({ theme }) => ({
  backgroundColor: 'rgba(0,0,0,.95)',
  position: 'fixed',
  zIndex: theme.zIndex.modal,
  inset: 0,
  width: '100%',
  height: '100%',
  animation: `${fadeIn} ${transitionDuration}ms ease-in-out`,
  '> *': {
    flexGrow: 0,
  },
  '.swipe-container': {
    position: 'relative',
    height: '52%',
  },
}));

const TransitionWrapper = styled('div')(() => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 0,
}));

const ImageWrapper = styled(Flex)({
  position: 'absolute',
  top: 0,
  height: '100%',
  width: '100%',
  // background: 'no-repeat center url(/static/images/placeholder/promoted.svg)',
  // backgroundSize: 'contain',
  transition: `transform ${transitionDuration}ms ease-in-out, opacity ${transitionDuration}ms ease-in-out`,
});

const ZoomedImage = styled(Image)(({ theme }) => ({
  maxHeight: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  backgroundSize: 'contain',
  borderRadius: theme.radius.default,
}));

const NavArrowsContainer = styled(Flex)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '100%',
    zIndex: 3,
    [`${IconButton}`]: {
      fontSize: theme.font.size(3),
    },
  },
}));

interface ContentGalleryProps {
  items: GalleryItem[];
}

interface ThumbRowProps extends ContentGalleryProps {
  cols?: ResponsiveCols;
  onClick: ThumbProps['onClick'];
  cancelPadding?: responsiveProps<SpacingIndex>;
  background?: ComponentProps<typeof Scroller>['background'];
  active?: string;
}

interface ThumbProps extends GalleryItem {
  onClick: (id: string) => void;
  active?: boolean;
}

interface ImageZoomProps {
  activeItem?: GalleryItem;
  onClick: (id: string) => void;
  onClose: () => void;
  items: ContentGalleryProps['items'];
}

interface NavArrowsProps {
  onClick: (diff: number) => void;
}

export default ContentGallery;
